/**
 * Confirmation dialog for cancelling a self-reported absence (#1767).
 * Collects the required reason before the irreversible cancel; the page runs
 * the mutation in `onConfirm`. Cancelled shifts are not restored.
 */

import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useLingui } from '@lingui/react';
import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { formatDateRange } from '@/lib/format';
import type { EmployeeAbsence } from '@/types/employeeAbsences';

/** Same cap as the backend `CancelReportRequest.reason` (`max=500`). */
const REASON_MAX_LENGTH = 500;

function getCancelReportSchema(i18n: {
  _: (descriptor: { id: string; message: string; values?: Record<string, unknown> }) => string;
}) {
  return z.object({
    reason: z
      .string()
      .trim()
      .min(1, {
        message: i18n._({
          id: 'absences.cancelReport.reasonRequired',
          message: 'Enter a reason.',
        }),
      })
      .max(REASON_MAX_LENGTH, {
        message: i18n._({
          id: 'absences.cancelReport.reasonTooLong',
          message: 'The reason can be at most {max} characters.',
          values: { max: REASON_MAX_LENGTH },
        }),
      }),
  });
}

type CancelReportFormValues = z.infer<ReturnType<typeof getCancelReportSchema>>;

export interface CancelReportDialogProps {
  open: boolean;
  absence: EmployeeAbsence | null;
  isPending: boolean;
  onConfirm: (reason: string) => void | Promise<void>;
  onOpenChange: (open: boolean) => void;
}

export function CancelReportDialog({
  open,
  absence,
  isPending,
  onConfirm,
  onOpenChange,
}: CancelReportDialogProps) {
  const { i18n } = useLingui();

  const schema = useMemo(
    () => getCancelReportSchema(i18n),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- i18n.locale, not the stable i18n instance
    [i18n.locale]
  );

  const form = useForm<CancelReportFormValues>({
    resolver: zodResolver(schema),
    defaultValues: { reason: '' },
  });

  // A fresh, empty reason every time the dialog opens for a row.
  useEffect(() => {
    if (open) {
      form.reset({ reason: '' });
    }
  }, [open, absence?.id, form]);

  const handleSubmit = async (values: CancelReportFormValues) => {
    await onConfirm(values.reason.trim());
  };

  const employeeName = absence?.employee
    ? `${absence.employee.firstname ?? ''} ${absence.employee.lastname ?? ''}`.trim()
    : '';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-md"
        onOpenAutoFocus={(event) => {
          // Focus the reason, not the first button: it is the one required input.
          event.preventDefault();
          form.setFocus('reason');
        }}
      >
        <DialogHeader>
          <DialogTitle>
            {i18n._({ id: 'absences.cancelReport.title', message: 'Cancel report' })}
          </DialogTitle>
          <DialogDescription>
            {i18n._({
              id: 'absences.cancelReport.description',
              message: 'The report is cancelled. Cancelled shifts are not restored.',
            })}
          </DialogDescription>
          {absence && (
            <div className="text-sm text-muted-foreground">
              {employeeName && <p className="font-medium text-foreground">{employeeName}</p>}
              <p>
                {formatDateRange(absence.from_date, absence.to_date, i18n.locale)}
              </p>
            </div>
          )}
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="reason"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {i18n._({ id: 'absences.cancelReport.reason', message: 'Reason' })}
                  </FormLabel>
                  <FormControl>
                    <Textarea {...field} rows={3} maxLength={REASON_MAX_LENGTH} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isPending}
              >
                {i18n._({ id: 'absences.cancelReport.dismiss', message: 'Keep report' })}
              </Button>
              <Button type="submit" variant="destructive" disabled={isPending}>
                {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />}
                {i18n._({ id: 'absences.cancelReport.confirm', message: 'Cancel report' })}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
